import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import './App.css';
import { CompareProvider } from './context/CompareContext';
import { MealPlanProvider } from './context/MealPlanContext';
import { CartProvider } from './context/CartContext';
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CompareBar from './components/CompareBar';
import ExitIntentPopup from './components/ExitIntentPopup';
import AffiliateTracker from './components/AffiliateTracker';

// Lazy-loaded pages
const HomePage = lazy(() => import('./pages/HomePage'));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const AffiliateProductPage = lazy(() => import('./pages/AffiliateProductPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CityPage = lazy(() => import('./pages/CityPage'));
const DeliveryAreasPage = lazy(() => import('./pages/DeliveryAreasPage'));
const BusinessDashboard = lazy(() => import('./pages/BusinessDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const DigestDashboard = lazy(() => import('./pages/DigestDashboard'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));
const AffiliateAnalytics = lazy(() => import('./pages/AffiliateAnalytics'));
const MealPlanPage = lazy(() => import('./pages/MealPlanPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PradoAdminPage = lazy(() => import('./pages/PradoAdminPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-400 font-medium">Loading...</span>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        <h1 className="text-6xl font-extrabold text-gray-900 mb-4">404</h1>
        <p className="text-gray-500 text-lg mb-6">Page not found</p>
        <Link to="/" className="inline-block bg-green-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-green-700 transition-colors">
          Back to Home
        </Link>
      </div>
    </main>
  );
}

const FULL_SCREEN_ROUTES = ['/dashboard', '/admin', '/checkout', '/order-confirmation'];

function AppInner() {
  const location = useLocation();
  const isFullScreen = FULL_SCREEN_ROUTES.some(r => location.pathname.startsWith(r));

  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <AffiliateTracker />
      <ExitIntentPopup />
      {!isFullScreen && <Navbar />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/meals/category/:categorySlug" element={<CategoryPage />} />
          <Route path="/meals/:slug" element={<AffiliateProductPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/delivery" element={<DeliveryAreasPage />} />
          <Route path="/delivery/:citySlug" element={<CityPage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/dashboard" element={<BusinessDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/affiliate" element={<AffiliateAnalytics />} />
          <Route path="/admin/prado" element={<PradoAdminPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/meal-plan" element={<MealPlanPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin/digest" element={<DigestDashboard />} />
          <Route path="/track" element={<TrackOrderPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
          <Route path="/reviews" element={<ReviewPage />} />
          <Route path="/author/:authorId" element={<AuthorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {!isFullScreen && <Footer />}
      <CompareBar />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <CompareProvider>
        <MealPlanProvider>
          <CartProvider>
            <BrowserRouter>
              <AppInner />
            </BrowserRouter>
          </CartProvider>
        </MealPlanProvider>
      </CompareProvider>
    </div>
  );
}

export default App;
